import { buildEntityColumns, entityModel, PASSWORD_DATA_TYPE } from '@/composables/entityColumns';
import { filterControlMappings } from '@/components/FilterBuilder/filterControlRegistry';
import { ID_LOOKUP_OPTIONS } from '@/constants/lookupFilterOptions';
import { isStatusColumn } from '@/components/DataTable/statusChips';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * The kind of a field decides how a record's Details draw its value. The
 * rules, the counts and the display for each kind are in
 * docs/specs/record-properties-panel.md. Tables do not use this. They keep
 * ColumnMapping.
 */
export const FIELD_KINDS = [
    'relatedRecord',
    'person',
    'relatedList',
    'status',
    'yesNo',
    'dateTime',
    'text',
    'longText',
    'code',
    'link',
    'email',
    'networkAddress',
    'timeZone',
    'listOfValues',
    'number',
    'count',
    'sizeOrRate',
    'percentage',
    'volume',
    'hourOfDay',
    'duration',
    'foreignKeyId',
    'lookupId',
    'recordId',
    'password',
] as const;

export type FieldKind = (typeof FIELD_KINDS)[number];

/** Kinds in one family share a component and differ only in formatting. */
export type FieldFamily = 'relatedRecord' | 'person' | 'status' | 'yesNo' | 'dateTime' | 'text' | 'number';

const FAMILY_OF_KIND: Record<FieldKind, FieldFamily> = {
    relatedRecord: 'relatedRecord',
    relatedList: 'relatedRecord',
    person: 'person',
    status: 'status',
    yesNo: 'yesNo',
    dateTime: 'dateTime',
    text: 'text',
    longText: 'text',
    code: 'text',
    link: 'text',
    email: 'text',
    networkAddress: 'text',
    timeZone: 'text',
    listOfValues: 'text',
    number: 'number',
    count: 'number',
    sizeOrRate: 'number',
    percentage: 'number',
    volume: 'number',
    hourOfDay: 'number',
    duration: 'number',
    foreignKeyId: 'number',
    lookupId: 'number',
    recordId: 'number',
    password: 'text',
};

export function fieldFamilyOf(kind: FieldKind): FieldFamily {
    return FAMILY_OF_KIND[kind];
}

/**
 * Sets the kind of a single field when the rules below guess wrong. Keyed by
 * models.json entity, then by field. Empty today: every field of the record
 * types with a page gets the right kind from the rules.
 */
export const fieldKindOverrides: Record<string, Record<string, FieldKind>> = {};

type KindColumn = Pick<TableColumnStaticProps, 'id' | 'key' | 'dataType'>;

/**
 * What a password field shows until someone presses its eye button: always
 * eight dots, so the dots do not give away how long the password is.
 */
export const PASSWORD_MASK = '••••••••';

/**
 * A login password that support reads out to a client, such as a contact
 * login's password. Every surface draws it as a password field: masked, with
 * a button that shows it. Which fields these are is decided in
 * `READABLE_PASSWORD_FIELDS`, which gives their columns this data type.
 */
export function isPasswordColumn(column: Pick<TableColumnStaticProps, 'dataType'>): boolean {
    return column.dataType === PASSWORD_DATA_TYPE;
}

/**
 * Name rules match whole words of the field's snake_case name, so "ip" finds
 * "remote_ip" but not "description", and "hour" finds "download_start_hour"
 * but not "artist_repeat_limit_hours", which is a number of hours.
 */
function hasWord(fieldName: string, words: readonly string[]): boolean {
    return fieldName.split('_').some((word) => words.includes(word));
}

const STRING_NAME_RULES: ReadonlyArray<{ kind: FieldKind; matches: (fieldName: string) => boolean }> = [
    { kind: 'longText', matches: (name) => hasWord(name, ['note', 'notes', 'description', 'message', 'messages', 'comment', 'comments']) },
    { kind: 'code', matches: (name) => hasWord(name, ['code', 'isrc', 'upc', 'dpid', 'key', 'serial']) },
    { kind: 'link', matches: (name) => hasWord(name, ['url', 'website']) },
    { kind: 'email', matches: (name) => hasWord(name, ['email']) },
    { kind: 'networkAddress', matches: (name) => hasWord(name, ['ip', 'mac', 'lan']) },
    { kind: 'timeZone', matches: (name) => name.includes('time_zone') },
];

const NUMBER_NAME_RULES: ReadonlyArray<{ kind: FieldKind; matches: (fieldName: string) => boolean }> = [
    { kind: 'count', matches: (name) => hasWord(name, ['count', 'total', 'votes']) },
    { kind: 'sizeOrRate', matches: (name) => hasWord(name, ['size', 'gb', 'bitrate']) },
    { kind: 'percentage', matches: (name) => hasWord(name, ['percent', 'percentage']) },
    { kind: 'volume', matches: (name) => hasWord(name, ['volume']) },
    { kind: 'hourOfDay', matches: (name) => hasWord(name, ['hour']) },
    { kind: 'duration', matches: (name) => hasWord(name, ['duration', 'seconds']) },
];

/** A model's name, such as `Location` or `Location[]`. `Date` is a type, not a model. */
function isModelName(dataType: string): boolean {
    return /^[A-Z]/.test(dataType) && dataType.replace('[]', '') !== 'Date';
}

/** A bundled id lookup, such as `bucket_type_id`, whose number stands for a name like "Dynamic". */
function isIdLookup(entityId: string, column: KindColumn): boolean {
    const optionsSource = filterControlMappings[entityId]?.[column.key]?.optionsSource;
    return optionsSource !== undefined && optionsSource in ID_LOOKUP_OPTIONS;
}

/**
 * A has-many relation. Most are typed as a model array, such as
 * `Account.locations` (`Location[]`). Some are typed as a single model but
 * listed in `_childrenMappings`, such as `Account.tags` (`Tag`), and the API
 * sends them as an array too.
 */
function isRelatedList(entityId: string, column: KindColumn): boolean {
    if (column.dataType.endsWith('[]')) {
        return isModelName(column.dataType);
    }
    const childrenMappings = entityModel(entityId)?._childrenMappings as Record<string, string> | undefined;
    return isModelName(column.dataType) && childrenMappings !== undefined && column.id in childrenMappings;
}

/**
 * The kind of one field. An override wins. Then the model's own type decides,
 * so a field called "location" that holds a Location is a related record.
 * Only then does the field's name decide, among strings and among numbers.
 * Every field gets a kind: anything no rule knows is plain text.
 */
export function fieldKindFor(
    entityId: string,
    column: KindColumn,
    overrides: Record<string, Record<string, FieldKind>> = fieldKindOverrides,
): FieldKind {
    const override = overrides[entityId]?.[column.id];
    if (override) {
        return override;
    }

    if (isPasswordColumn(column)) {
        return 'password';
    }

    const fieldName = column.id;
    const dataType = column.dataType;

    // A plain `*_status_id` number is the status's ID, not the status. Only a
    // bundled lookup's ID reads as the status word, as in tables today.
    if (isStatusColumn(fieldName) && (!fieldName.endsWith('_id') || isIdLookup(entityId, column))) {
        return 'status';
    }
    if (isRelatedList(entityId, column)) {
        return 'relatedList';
    }
    if (dataType === 'User') {
        return 'person';
    }
    if (isModelName(dataType)) {
        return 'relatedRecord';
    }
    if (dataType === 'boolean') {
        return 'yesNo';
    }
    if (dataType === 'Date') {
        return 'dateTime';
    }
    if (dataType.endsWith('[]')) {
        return 'listOfValues';
    }
    if (dataType === 'string') {
        return STRING_NAME_RULES.find((rule) => rule.matches(fieldName))?.kind ?? 'text';
    }
    if (dataType === 'number') {
        if (fieldName === 'id') {
            return 'recordId';
        }
        if (isIdLookup(entityId, column) || fieldName.endsWith('_status_id') || fieldName.endsWith('_type_id')) {
            return 'lookupId';
        }
        if (fieldName.endsWith('_id')) {
            return 'foreignKeyId';
        }
        return NUMBER_NAME_RULES.find((rule) => rule.matches(fieldName))?.kind ?? 'number';
    }
    return 'text';
}

/** Words a label keeps in capitals, because they are said letter by letter. */
const ACRONYMS = new Set(['id', 'ids', 'ip', 'mac', 'lan', 'url', 'dns', 'gb', 'isrc', 'upc', 'dpid', 'ddex', 'ean', 'icpn', 'nrm', 'tpg', 's3', 'utc']);

/** "Account Manager User ID" becomes "Account manager user ID". */
export function sentenceCaseLabel(header: string): string {
    return header
        .split(' ')
        .filter((word) => word !== '')
        .map((word, index) => {
            // A plural acronym keeps its small "s": "Client user IDs", not "IDS".
            if (word.toLowerCase() === 'ids') {
                return 'IDs';
            }
            if (ACRONYMS.has(word.toLowerCase())) {
                return word.toUpperCase();
            }
            return index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word.toLowerCase();
        })
        .join(' ');
}

function snakeCaseOf(entityId: string): string {
    return entityId.replace(/(?<!^)[A-Z]/g, '_$&').toLowerCase();
}

/**
 * The label a field shows in Details. A label the person typed in Customize
 * shows exactly as typed. A generated one is sentence case, with two
 * shortenings from the spec:
 *
 * - A person drops the trailing "user": "Account manager", not "Account
 *   manager user". Its ID row keeps the word: "Account manager user ID".
 * - A record's own status is just "Status": on an account, "Account status"
 *   reads "Status", and "Account status ID" reads "Status ID".
 */
export function propertyLabel(entityId: string, column: Pick<TableColumnStaticProps, 'id' | 'header'>, kind: FieldKind): string {
    const generatedHeader = buildEntityColumns(entityId).find((candidate) => candidate.id === column.id)?.header;
    if (generatedHeader !== undefined && generatedHeader !== column.header) {
        return column.header;
    }
    const ownStatusPrefix = `${snakeCaseOf(entityId)}_status`;
    if (column.id === ownStatusPrefix) {
        return 'Status';
    }
    if (column.id === `${ownStatusPrefix}_id`) {
        return 'Status ID';
    }
    const label = sentenceCaseLabel(column.header);
    if (kind === 'person' && / user$/.test(label)) {
        return label.slice(0, -' user'.length);
    }
    return label;
}

/** The key of the row a column reads: `account_status.display_string` reads `account_status`. */
export function fieldKeyOf(column: Pick<TableColumnStaticProps, 'key'>): string {
    return column.key.split('.')[0];
}

/** The raw value behind a column: `account_status.display_string` reads `row.account_status`. */
export function fieldValueOf(row: Record<string, unknown>, column: Pick<TableColumnStaticProps, 'key'>): unknown {
    return row[fieldKeyOf(column)];
}

/**
 * True when a field has nothing to show, so its row keeps its place and shows
 * a dash. False, zero and an unknown status word are values.
 */
export function isEmptyFieldValue(row: Record<string, unknown>, column: Pick<TableColumnStaticProps, 'key'>): boolean {
    return isEmptyValue(fieldValueOf(row, column));
}

/**
 * True when a value has nothing to show: null, missing, a blank string or an
 * empty list. The combined record of several rows uses the same test, so a
 * field it calls empty is one each record would show as a dash.
 */
export function isEmptyValue(value: unknown): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    return false;
}
